
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { developers as developersList } from "@/services/developersData";
import { getDevelopers } from "@/services/property";
import SEO from "@/components/seo/seo";

// The developersList contains the static info from developersData

const Developers = () => {
  const [mappedDevelopers, setMappedDevelopers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAndMapDevelopers = async () => {
      try {
        const data = await getDevelopers();
        const fetchedDevelopers = data?.developers ?? [];

        // Map developersList with new .properties field (the count, mapped by first word)
        const mapped = developersList.map((dev) => {
          const devFirstWord = dev.name.split(" ")[0].toLowerCase();
          // Try to find in fetchedDevelopers by matching first word, case insensitive
          const match = fetchedDevelopers.find((item: any) => {
            const itemDevFirstWord = (item.developer || "").split(" ")[0].toLowerCase();
            return devFirstWord === itemDevFirstWord;
          });
          return {
            ...dev,
            properties: match ? match.count : 0,
          };
        });

        // Sort by properties count in descending order
        const sortedMapped = [...mapped].sort((a, b) => b.properties - a.properties);

        setMappedDevelopers(sortedMapped);
      } catch (error) {
        console.log("Error while fetching developers", error);
        // fallback: just use list without count, no sort needed as all will be 0
        setMappedDevelopers(developersList.map(dev => ({ ...dev, properties: 0 })));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndMapDevelopers();
  }, []);

  return (
    <>
      <SEO
        title="Top Dubai Developers | Urban Oasis UAE"
        description="Discover leading property developers in Dubai."
        url="/developers"
        image="/image/logo.png"
        keywords="dubai real estate, urban oasis"
      />
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Top Dubai Developers</h1>
            <p className="text-muted-foreground max-w-2xl">
              Explore Dubai's premier property developers known for their iconic projects and
              commitment to excellence in real estate development.
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-10 w-full" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mappedDevelopers.map((developer) => (
                <Card key={developer.slug} className="overflow-hidden transition-all hover:shadow-lg">
                  <div className="aspect-video w-full overflow-hidden">
                    <img
                      src={developer.projectImage || "/placeholder.svg"}
                      alt={developer.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle>{developer.name}</CardTitle>
                    <CardDescription>
                      {developer.properties} Properties
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p>{developer.about}</p>
                  </CardContent>
                  <CardFooter>
                    <Button asChild className="w-full">
                      <Link to={`/developers/${developer.slug}`}>View Properties</Link>
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Developers;
